import { Component, effect, HostListener, inject, Input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { AsyncPipe, DecimalPipe, formatNumber, NgClass } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { BetPlacementService } from "../../../../../../../../shared/src/lib/services/bet-placement.service";
import { SnackbarService } from "../../../../../../../../shared/src/lib/services/snackbar.service";
import { BehaviorSubject, combineLatest, combineLatestWith, filter, Observable, Subject } from "rxjs";
import { LiveUpdateService } from "../../../../../../../../shared/src/lib/services/live-update.service";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { CoreService } from "../../../../../../../../shared/src/lib/services/core.service";
import { TranslateModule } from "@ngx-translate/core";
import { safeParseFloat } from "../../../../../../../../shared/src/lib/utils/common";
import { validOdds } from "../../../../../../../../shared/src/lib/constants/valid-odds";
import { LoaderService } from "../../../../../../../../shared/src/lib/services/loader.service";
import { FeatureFlagDirective } from "../../../../../../../../shared/src/lib/directives/feature-flag.directive";
import { Market, MarketProvider, PredictedBet } from "../../../../../../../../shared/src/lib/types/bet.type";
import { BetService } from "../../../../../../../../shared/src/lib/services/bet.service";
import { ExposureService } from "../../../../../../../../shared/src/lib/services/exposure.service";
import { DebuggingService } from "../../../../../components/debugging-modal/services/debugging.service";
import { PlacementOpsService } from "../../../placement-manager/placement-ops.service";
import { BalanceService } from "../../../../../../../../shared/src/lib/services/balance.service";
import { map, takeUntil } from "rxjs/operators";
import { ExposureDebuggingObject } from "../../../../../components/debugging-modal/types/debugging.type";
import { AhLadderPnlComponent } from "../../../../content/market-layout/market/asian-handicap-pnl/ah-ladder-pnl/ah-ladder-pnl.component";
import { FeatureFlagService } from "../../../../../../../../shared/src/lib/services/feature-flag.service";
import { ToasterService } from "../../../../../../../../shared/src/lib/services/toaster.service";
import { RouterLink } from "@angular/router";
import { WhiteLabelConfigService } from "../../../../../../../../shared/src/lib/services/white-label-config.service";

@Component({
  selector: "app-ub-update-form",
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    NgClass,
    DecimalPipe,
    MatIconModule,
    MatProgressBarModule,
    TranslateModule,
    FeatureFlagDirective,
    AsyncPipe,
    AhLadderPnlComponent,
    RouterLink,
  ],
  templateUrl: "./ub-update-form.component.html",
  styleUrl: "./ub-update-form.component.scss",
})
export class UbUpdateFormComponent implements OnInit, OnDestroy, OnChanges {
  @HostListener("document:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.priceButtonFocus) return;

    if (event.key === "ArrowUp") {
      this.handlePriceIncrement();
      event.preventDefault();
    } else if (event.key === "ArrowDown") {
      this.handlePriceDecrement();
      event.preventDefault();
    }
  }
  @Input() initialData: any = null;
  @Input() matchType: any;
  @Input({ required: true }) betMarkets!: Record<string, Market>;
  bfBetId = signal<string>("");
  isBalanceInsufficient = false;
  readonly destroy$ = new Subject<void>();
  betPlacementService = inject(BetPlacementService);
  snackbar = inject(SnackbarService);
  liveUpdateService = inject(LiveUpdateService);
  form: FormGroup;
  liability = 0;
  potentialProfit: number = 0;
  alert: any;
  loader = inject(LoaderService);
  coreService = inject(CoreService);
  priceButtonFocus: boolean = false;
  toaster = inject(ToasterService);
  placementOps = inject(PlacementOpsService);
  balanceService = inject(BalanceService);
  featureFlagService = inject(FeatureFlagService);
  wlConfigService = inject(WhiteLabelConfigService);
  currentPrice: any = null;
  predictedBets$ = new BehaviorSubject<Record<string, PredictedBet>>({}); // Holds bet information to be shown as prediction. The string is bet ID
  $loading!: Observable<boolean>;
  disableActions = signal<boolean>(false);

  constructor(
    private fb: FormBuilder,
    private readonly betService: BetService,
    private readonly exposureService: ExposureService,
    protected readonly debuggingService: DebuggingService,
  ) {
    effect(() => {
      if (!this.bfBetId()) return;
      const data = this.liveUpdateService._getData("bet_result", {
        value: this.bfBetId(),
      });
      if (data) {
        console.log("Cancellation result ➡️ ", data);
        this.handleCancellationResult(data);
      }
    });
    this.form = this.fb.group({
      price: ["", [Validators.required, this.validOddValidator(), Validators.min(1.01), Validators.max(1000)]],
      stake: [{ value: "", disabled: true }, [Validators.required, Validators.maxLength(7)]],
      persistenceType: ["LAPSE", [Validators.required]],
    });
  }

  get price() {
    return this.form.get("price");
  }

  get stake() {
    return this.form.get("stake");
  }

  get persistenceType() {
    return this.form.get("persistenceType");
  }

  get isAsianHandicap() {
    return this.initialData?.bettingType?.includes("ASIAN_HANDICAP");
  }

  get isGreyhound() {
    return this.initialData?.eventTypeId === "4339";
  }

  get priceChanged() {
    return this.currentPrice !== safeParseFloat(this.price?.value);
  }

  ngOnInit() {
    const updatePermission = this.wlConfigService.showFeature("updateUnmatchedBet");
    const persistencePermission = this.wlConfigService.showFeature("persistenceType");
    const disableUnmatchedBets = this.wlConfigService.showFeature("disableUnmatchedBets");
    if (!updatePermission) {
      this.persistenceType?.disable({ emitEvent: false });
      this.price?.disable({ emitEvent: false });
    }
    if (disableUnmatchedBets) {
      this.price?.disable({ emitEvent: false });
    }

    if (!persistencePermission) this.persistenceType?.disable({ emitEvent: false });

    this.$loading = combineLatest([
      this.loader.isLoading("current-user-order"),
      this.loader.isLoading("refresh_exposure"),
      this.loader.isLoading("bf_cancelbet"),
      this.loader.isLoading("bf_updatebet"),
    ]).pipe(
      map(([loadingBets, refreshingExposure, cancelling, updating]) => {
        const loading = loadingBets || refreshingExposure || cancelling || updating;
        if (loading) {
          this.persistenceType?.disable({ emitEvent: false });
        } else if (updatePermission && persistencePermission) {
          this.persistenceType?.enable({ emitEvent: false });
        }
        return loading;
      }),
    );
    this.preparePredictedBetsOnFormValueChange();

    this.predictedBets$
      .pipe(combineLatestWith(this.exposureService.balanceData, this.betService.betsMarkets$, this.betService.nonPendingBets$))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([predictedBets, balance, _, nonPendingBets]) => {
          const marketGroupId = this.betService.generateMarketGroupIdForCalculationBet(this.initialData);
          if (
            Boolean(balance) &&
            Boolean(this.initialData) &&
            Boolean(this.initialData.betId) &&
            Boolean(predictedBets[marketGroupId]) &&
            Boolean(predictedBets[marketGroupId].stake) &&
            Boolean(predictedBets[marketGroupId].fOdds)
          ) {
            this.isBalanceInsufficient = Boolean(
              this.betService.evaluateExposureValidation(
                nonPendingBets.map((bet) => ({
                  ...bet,
                  betSize: bet.stakeRemaining === 0 ? (bet.stakeMatched ? bet.stakeMatched : bet.betSize) : bet.betSize,
                  marketGroupId: this.betService.generateMarketGroupIdForCalculationBet(bet),
                  dfOdds: bet.provider === MarketProvider.BF && bet.bettingType === "LINE" ? bet.averagePriceMatched : bet.dfOdds,
                  rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
                })),
                balance!,
                predictedBets[this.betService.generateMarketGroupIdForCalculationBet(this.initialData)],
                this.initialData.betId,
              ),
            );
          } else {
            this.isBalanceInsufficient = false;
          }
        },
      });
    if (this.initialData) this.bfBetId.set(this.initialData?.betId);
    if (this.isAsianHandicap) {
      this.price?.clearValidators();
    }
    this.placementOps.currentParams = {
      type: this.initialData?.bettingType,
      data: {
        PROVIDER: this.initialData?.provider || "BF",
      },
    };
    const formData = {
      price: safeParseFloat(this.initialData?.stakeMatched) === 0 ? this.initialData?.priceSize?.price : this.initialData?.averagePriceMatched,
      stake: formatNumber(this?.initialData?.betSize, "en", "1.0-2"),
      persistenceType: this.initialData?.persistenceType,
    };
    this.currentPrice = formData?.price;
    this.form.setValue(formData);
    this.initProfitLoss(this.initialData);
    this.price?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.preventNonNumeric("price", value);
      if (this.currentPrice !== Number(value)) {
        this.persistenceType?.disable({ emitEvent: false });
        this.markPrice();
      } else {
        this.enablePriceAndPersistence();
        this.resetPriceMark();
      }
    });
    this.stake?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.preventNonNumeric("stake", value);
    });
    this.persistenceType?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (this.initialData?.persistenceType !== value) {
        this.price?.disable({ emitEvent: false });
      } else {
        this.enablePriceAndPersistence();
      }
    });
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.alert = null;
      this.updateCalculations(values.price);
      this.betPlacementService.currentPlacement.next({
        ...values,
        PRICE: this.initialData?.priceSize?.price,
        SIZE: this.initialData?.priceSize?.size,
        SIDE: this.initialData?.side,
        MARKET_ID: this.initialData?.marketId,
        SPORT_ID: this.initialData?.eventTypeId,
        EVENT_ID: this.initialData?.eventId,
        PERSISTENCE_TYPE: values?.persistenceType,
        marketName: this.initialData?.marketName,
        eventName: this.initialData?.eventName,
        SELECTION_ID: this.initialData?.selectionId,
        HANDICAP: this.initialData?.handicap,
        runnerName: this.initialData?.runnerName,
      });
    });

    this.liveUpdateService
      .getData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => {
        if (update?.id === this.initialData?.marketId) {
          const runner = update?.r?.find((item: any) => item?.id === this.initialData?.selectionId);
          if (runner) {
            const currentOdd = this.initialData?.stakeMatched === 0 ? this.initialData?.priceSize?.price : this.initialData?.averagePriceMatched;
            const oddList = this.initialData?.side === "BACK" ? runner?.ex?.abp : runner?.ex?.alp;
            if (oddList?.some((price: number) => price === currentOdd)) {
              this.balanceService.refreshExposure();
            }
          }
        }
      });

    this.betPlacementService.changeNotifier$.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      if (type === "multipleCancellation") this.onCancel();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["initialData"]) this.enablePriceAndPersistence();
  }

  enablePriceAndPersistence = () => {
    this.persistenceType?.enable({ emitEvent: false });
    if (!this.wlConfigService.showFeature("disableUnmatchedBets")) {
      this.price?.enable({ emitEvent: false });
    }
  };

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  validOddValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = parseFloat(control.value);
      return validOdds.includes(value) ? null : { invalidOdd: true };
    };
  }

  preventNonNumeric(field: string, value: string): void {
    if (!value) return;

    const cleanedValue = String(value).replace(/[^0-9.]+/g, "");
    if (cleanedValue !== value) {
      this.form.patchValue({ [field]: cleanedValue }, { emitEvent: false });
    }
  }

  onPriceBlur(): void {
    const numericValue = parseFloat(this.form.value?.price);
    if (isNaN(numericValue) || this.isAsianHandicap) {
      return;
    }

    const closestValidValue = this.findClosest(numericValue, validOdds);
    if (closestValidValue !== numericValue) {
      this.form.patchValue({ price: closestValidValue }, { emitEvent: true });
    }
  }

  findClosest(num: number, arr: number[]): number {
    return arr.reduce((prev, curr) => (Math.abs(curr - num) < Math.abs(prev - num) ? curr : prev));
  }

  handlePriceIncrement() {
    if (this.price?.disabled) return;
    const currentPrice = this.form.value?.price;
    const numericPrice = parseFloat(currentPrice);
    if (isNaN(numericPrice)) {
      return;
    }

    const nextHigher = validOdds.find((odds) => odds > numericPrice);
    if (nextHigher !== undefined) {
      this.form.patchValue({ price: nextHigher }, { emitEvent: true });
    }
  }

  handlePriceDecrement() {
    if (this.price?.disabled) return;
    const currentPrice = this.form.value?.price;
    const numericPrice = parseFloat(currentPrice);
    if (isNaN(numericPrice)) {
      return;
    }

    const index = validOdds.findIndex((odds) => odds >= numericPrice);
    const nextLower = index > 0 ? validOdds[index - 1] : undefined;
    if (nextLower !== undefined) {
      this.form.patchValue({ price: nextLower }, { emitEvent: true });
    }
  }

  handleCancellationResult(result: any) {
    this.disableActions.set(false);
    if (result?.data?.status !== "SUCCESS") {
      this.handleError(result?.data?.bf_message);
    }
  }

  handleSuccess(msg?: string) {
    this.toaster.success("Success", msg ?? "");
  }

  handleError(msg?: string) {
    this.toaster.error("Error", msg || "Unable to cancel your bet.");
  }

  handleSubmit() {
    if (this.form.valid) {
      this.disableActions.set(true);
      const data = {
        ID: this.initialData?.customerOrderRef,
        BET_ID: this.initialData?.betId,
        PRICE: this.price?.value,
        SIZE: this.stake?.value,
        F_ODDS: this.price?.value,
        PERSISTENCE_TYPE: this.persistenceType?.value,
      };

      // console.log(data);

      this.betPlacementService
        .updatePlacement(data)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            // console.log(response);
            if (response.status === "ok") {
              this.handleSuccess("Bet update submitted.");
              setTimeout(() => {
                this.betPlacementService.setRefresh(true);
                this.disableActions.set(false);
              }, 2000);
            } else {
              this.disableActions.set(false);
              this.handleError(JSON.stringify(response?.result));
            }
          },
          error: (err) => {
            console.error(err);
            this.disableActions.set(false);
          },
        });
    } else {
      this.snackbar.openSnackbar({
        message: `Form is not valid ${this.form.errors || ""}`,
      });
      console.warn("Form is not valid:", this.form.errors);
    }
  }

  onCancel() {
    this.disableActions.set(true);
    this.betPlacementService
      .cancelBet({ BET_ID: this.initialData?.betId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // console.log(response);
          if (response?.status === "ok") {
            if (!this.betPlacementService.isCancellingMultiple) this.handleSuccess("Bet cancellation submitted successfully.");
          } else {
            this.handleError(JSON.stringify(response?.result));
            this.disableActions.set(false);
          }
          this.betPlacementService.setUnmatchedBets(this.initialData?.betId);
          if (!this.betPlacementService.isCancellingMultiple) this.balanceService.refreshExposure();
        },
        error: (err) => {
          console.error(err);
          this.disableActions.set(false);
        },
      });
  }

  initProfitLoss(initialData: any) {
    this.potentialProfit = initialData?.side === "BACK" ? safeParseFloat(initialData?.potentialProfit) : safeParseFloat(initialData?.potentialLoss);
  }

  updateCalculations(price: number) {
    const stake = this.initialData?.betSize;

    if (this.price?.disabled) price = this.currentPrice;

    if (!price) {
      this.liability = 0;
      this.potentialProfit = 0;
      return;
    }

    // console.log(this.initialData);

    this.potentialProfit = (price - 1) * stake;
  }

  markPrice() {
    this.price?.markAsTouched();
    this.price?.markAsDirty();
  }

  resetPriceMark() {
    this.price?.markAsUntouched();
    this.price?.markAsPristine();
  }

  get userBalance() {
    const userBalance = this.balanceService.getUserBalance();
    return safeParseFloat(userBalance?.BALANCE);
  }

  protected readonly safeParseFloat = safeParseFloat;

  preparePredictedBetsOnFormValueChange() {
    this.form.valueChanges
      .pipe(
        filter(
          (formValues) =>
            Boolean(this.initialData) &&
            Boolean(this.initialData.betId) &&
            Boolean(this.initialData.bettingType) &&
            Boolean(this.initialData.marketId) &&
            Boolean(this.initialData.selectionId) &&
            Boolean(this.initialData.side) &&
            Boolean(this.initialData.priceSize) &&
            Boolean(this.initialData.priceSize.price) &&
            Boolean(formValues.price) &&
            Boolean(formValues.stake || this.initialData?.betSize),
        ),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (formValues) => {
          // console.log({params: this.initialData, provider: this.initialData.provider})
          const predictedBet: PredictedBet = {
            marketId: this.initialData.marketId,
            marketName: this.initialData.marketName,
            selectionId: Number(this.initialData.selectionId),
            side: this.initialData.side,
            fOdds: this.betService.getFinalOdds(
              Number(formValues.price),
              this.initialData.provider ?? MarketProvider.BF,
              this.initialData.bettingType === "LINE",
            ),
            stake: Number(formValues.stake ?? this.initialData?.betSize),
            bettingType: this.initialData.bettingType,
            marketGroupId: this.betService.generateMarketGroupIdForCalculationBet(this.initialData),
            provider: this.initialData.provider,
            handicap: this.initialData.handicap,
            handicapValues: this.initialData.handicapValues,
            averagePriceMatched: this.initialData.averagePriceMatched,
            runnerName: this.initialData.runnerName,
          };

          this.predictedBets$.next({
            ...this.predictedBets$.getValue(),
            [predictedBet.marketGroupId]: predictedBet,
          });
        },
      });
  }

  showDebugDialog(event: Event) {
    event.stopPropagation();

    const message = JSON.stringify(this.debuggingService.exposureDebuggingObject.getValue());
    this.debuggingService.showDebuggingAlert(message);
  }

  getTotalExposureDebugging(debuggingObjects: Record<string, ExposureDebuggingObject>) {
    if (this.initialData && this.initialData.eventName && this.initialData.marketName && this.initialData.betId) {
      const key = `${this.initialData.eventName} - ${this.initialData.marketName} - ${this.initialData.betId}`.toUpperCase();
      return debuggingObjects[key]?.newTotalExposure;
    }

    return 0;
  }
}
